import {
  Component, ChangeDetectionStrategy, viewChild,
  inject, signal, computed, effect, DestroyRef
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  Subject, EMPTY, of,
  filter, map, tap, delay, withLatestFrom, distinctUntilChanged,
  switchMap, take, timeout, mergeMap
} from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { zefGo } from '@zerops/zef/ngrx-router';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { formValueOnValid } from '@zerops/zef/forms';
import { TerminalFeature } from '@zerops/zerops/feature/terminal';
import { ServiceStackEntity } from '@zerops/zerops/core/service-stack-base';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import { ZagentAgentType, AGENT_DISPLAY_NAMES } from '@zerops/zerops/core/zerops-services';
import { AgentStripEntry, AgentStripStatus } from '@zerops/zui/agent-strip';
import {
  FEATURE_NAME,
  AGENT_AUTH_PAUSE_MS,
  TERMINAL_CONNECT_TIMEOUT_MS,
  DIALOG_OPEN_TERMINAL_MOUNT_DELAY_MS,
  DIALOG_CLOSE_RESET_DELAY_MS,
  COPY_FEEDBACK_DURATION_MS,
  SEND_CODE_NEWLINE_DELAY_MS,
  TERMINAL_REMOUNT_DELAY_MS,
  POST_AUTH_KEY_DELAY_MS
} from './zcp-agent-auth-dialog.constant';
import {
  AuthFlowState, AuthFlowPhase, ZcpAgentAuthDialogMeta, ZcpAgentAuthDialogSuccessNavigation
} from './zcp-agent-auth-dialog.model';
import { getAuthHandler, AgentAuthHandler } from './zcp-agent-auth-dialog.handlers';
import { zcpAgentAuthDialogActions } from './zcp-agent-auth-dialog.state';
import {
  parseTerminalOutput, matchAuthUrl, matchCompletedToken, DEVICE_CODE_CONTINUATION
} from './zcp-agent-auth-dialog.output-parser';
import { ZcpAgentAuthDialogForm } from './zcp-agent-auth-dialog.form';

/** Stall timer duration — sends enter when no pattern matches for this long */
const STALL_TIMEOUT_MS = 1000;

@Component({
  selector: 'z-zcp-agent-auth-dialog',
  templateUrl: './zcp-agent-auth-dialog.feature.html',
  styleUrl: './zcp-agent-auth-dialog.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZcpAgentAuthDialogFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #document = inject(DOCUMENT);
  #destroyRef = inject(DestroyRef);
  #form = inject(ZcpAgentAuthDialogForm);
  #serviceStackEntity = inject(ServiceStackEntity);
  #projectEntity = inject(ProjectEntity);

  // # View children
  terminalRef = viewChild(TerminalFeature);

  // # Event Streams
  onClose$ = new Subject<void>();
  onStartAuth$ = new Subject<void>();
  onCancel$ = new Subject<void>();
  onOpenUrl$ = new Subject<void>();
  onCopyUrl$ = new Subject<void>();
  onCopyCode$ = new Subject<void>();
  onSendCode$ = new Subject<void>();
  onRetry$ = new Subject<void>();
  onSkip$ = new Subject<void>();           // Walker: skip the current agent, advance to next
  onGoToControlPanel$ = new Subject<void>(); // Walker: final terminal state CTA
  onTerminalOutput$ = new Subject<string>();
  onAuthComplete$ = new Subject<void>();
  onDialogOpened$ = new Subject<void>();

  // # Data — sync
  key = FEATURE_NAME;

  // # Data — async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  meta$ = this.dialogState$.pipe(
    map((data) => data?.meta as ZcpAgentAuthDialogMeta | undefined)
  );
  serviceStackId$ = this.meta$.pipe(map((m) => m?.serviceStackId));
  containerId$ = this.meta$.pipe(map((m) => m?.containerId));
  agentType$ = this.meta$.pipe(map((m) => m?.agentType));
  // Absent/undefined defaults to 'control-plane' — the legacy unconditional
  // navigate-on-success behavior every pre-existing caller keeps.
  successNavigation$ = this.meta$.pipe(map((m) => m?.successNavigation ?? 'control-plane'));
  formState$ = this.#form.state$;

  // Service stack + project context for the heading — resolved from the
  // meta's serviceStackId. The service name comes straight off the stack;
  // the project name goes through ProjectEntity (the embedded `project` on
  // a stack isn't reliably populated in the store — same path the service
  // card base uses).
  serviceStack$ = this.serviceStackId$.pipe(
    filter((id): id is string => !!id),
    distinctUntilChanged(),
    switchMap((id) => this.#serviceStackEntity.entityById$(id))
  );
  serviceName$ = this.serviceStack$.pipe(map((s) => s?.name));
  projectName$ = this.serviceStack$.pipe(
    map((s) => s?.projectId),
    filter((id): id is string => !!id),
    distinctUntilChanged(),
    switchMap((id) => this.#projectEntity.entityById$(id)),
    map((p) => p?.name)
  );

  // # Auth flow state — signal-based for synchronous reads in output handler
  terminalReady = signal(false);

  // Single source of truth for auth-flow state.
  authState = signal<AuthFlowState>({ phase: 'idle' });

  // Derived read-only signals — preserved so the template, stepper math, and
  // existing reads (`this.authPhase()`, etc.) continue to work without changes.
  authPhase = computed(() => this.authState().phase);
  authUrl = computed(() => {
    const s = this.authState();
    return ('authUrl' in s) ? s.authUrl : null;
  });
  // Device-code (Codex flow only). Only populated during 'waiting_for_code'
  // when the active handler is in device-code flow mode. Null for paste-code
  // (Claude) — the template branches on this + flow mode jointly.
  deviceCode = computed<string | null>(() => {
    const s = this.authState();
    return (s.phase === 'waiting_for_code' && s.deviceCode) ? s.deviceCode : null;
  });
  errorMessage = computed(() => {
    const s = this.authState();
    return s.phase === 'error' ? s.message : null;
  });

  copied = signal(false);
  copiedCode = signal(false);

  // # Walker state (Phase 2-b) — agent queue + per-agent statuses.
  // Single-agent flows naturally collapse to a 1-element queue.
  agentQueue = signal<ZagentAgentType[]>([]);
  currentAgentIdx = signal(0);
  agentStatuses = signal<Record<string, AgentStripStatus>>({});

  // Clamped index — defensive guard against an in-flight queue shrinkage
  // where `currentAgentIdx` could briefly exceed `agentQueue.length - 1`
  // (e.g., `meta$` re-emits a shorter queue before the dispatcher tap
  // resets the idx). Used by every derived signal that indexes into the
  // queue so the strip and active-agent reads stay internally consistent.
  #safeAgentIdx = computed(() => {
    const q = this.agentQueue();
    const i = this.currentAgentIdx();
    if (q.length === 0) return 0;
    return Math.max(0, Math.min(i, q.length - 1));
  });

  // The agent currently being processed by the inner state machine.
  activeAgent = computed<ZagentAgentType | null>(() => {
    const q = this.agentQueue();
    if (q.length === 0) return null;
    return q[this.#safeAgentIdx()] ?? null;
  });

  // True when there is exactly one agent in the queue — the existing
  // single-agent UX (auto-close after success, no strip needed) collapses
  // to this path naturally.
  isSingleAgent = computed(() => this.agentQueue().length === 1);

  // Walker has reached the last agent in the queue. Used to swap
  // `[Skip to <next>]` for `[Skip to Complete]` / suppress next-agent
  // language in the failure CTA. Uses the clamped index so a transient
  // out-of-bounds idx doesn't flip this to a stale value.
  isLastAgent = computed(() => this.#safeAgentIdx() === this.agentQueue().length - 1);

  // Final terminal state — every agent has a terminal status (done/failed/skipped)
  // AND we've reached the last agent OR an active agent terminal state is set.
  walkerComplete = computed(() => {
    const queue = this.agentQueue();
    if (queue.length === 0) return false;
    const statuses = this.agentStatuses();
    return queue.every((t) => {
      const s = statuses[t];
      return s === 'done' || s === 'failed' || s === 'skipped';
    });
  });

  // Whether at least one agent in the queue succeeded — drives the `authSuccess`
  // bit on close meta and the navigate-to-control-plane effect.
  hasAnySuccess = computed(() => {
    const statuses = this.agentStatuses();
    return Object.values(statuses).some((s) => s === 'done');
  });

  // The next agent in line — used in the failure CTA copy ("Skip to <next>").
  // Returns null when the active agent is the last one. Uses the clamped idx.
  nextAgentName = computed<string | null>(() => {
    const q = this.agentQueue();
    const i = this.#safeAgentIdx();
    const next = q[i + 1];
    return next ? AGENT_DISPLAY_NAMES[next] : null;
  });

  // Strip entries — derived from queue + per-agent statuses, fed into
  // `<zui-agent-strip>` as a single input. Active agent is the one at the
  // clamped idx — guarding against out-of-bounds reads if the queue ever
  // shrinks under us.
  stripEntries = computed<AgentStripEntry[]>(() => {
    const q = this.agentQueue();
    const statuses = this.agentStatuses();
    const safeIdx = this.#safeAgentIdx();
    return q.map((t, i) => ({
      agentType: t,
      status: (statuses[t] as AgentStripStatus | undefined)
        ?? (i === safeIdx ? 'active' : 'pending')
    }));
  });

  // Active handler — derived from activeAgent. Replaces the previous
  // `activeHandler` signal that was set imperatively from `meta$`.
  activeHandler = computed<AgentAuthHandler | null>(() => {
    const a = this.activeAgent();
    return a ? (getAuthHandler(a) ?? null) : null;
  });

  // Drives which UI branch renders in `waiting_for_code` phase: paste-code
  // (Claude form) vs device-code (Codex prominent-code display).
  isDeviceCodeFlow = computed(() => this.activeHandler()?.flowMode === 'device-code');

  // url-poll (Cursor): auto-open URL, CLI self-polls, no code in either
  // direction — nothing for the user to submit in the dialog.
  isUrlPollFlow = computed(() => this.activeHandler()?.flowMode === 'url-poll');

  // Both device-code and url-poll are "poll" flows: the user finishes the
  // handshake in their browser, so the dialog has no code-submission step
  // and renders the browser-side "waiting…" card instead of a paste form.
  isPollFlow = computed(() => this.isDeviceCodeFlow() || this.isUrlPollFlow());

  // Computed display name from active agent
  agentDisplayName = computed(() => {
    const a = this.activeAgent();
    return a ? AGENT_DISPLAY_NAMES[a] : 'Agent';
  });

  // # Stepper state — latch the most-recent non-error phase so the error overlay
  // renders against the correct step. Includes 'idle' on purpose: Cancel/Retry
  // transition through idle and must reset the latch so a subsequent error from
  // 'starting' doesn't render against a stale phase from the previous run.
  lastNonErrorPhase = signal<AuthFlowPhase>('idle');

  currentStepIndex = computed(() => {
    const p = this.authPhase() === 'error' ? this.lastNonErrorPhase() : this.authPhase();
    // Poll flows (Codex device-code, Cursor url-poll) skip the
    // `submitting_code` phase entirely — the user finishes in their
    // browser, not the dialog — so the step list is one shorter (no
    // "Verify code"). 'complete' returns the full step count so every row
    // renders done; there's no active row in the success terminal state.
    if (this.isPollFlow()) {
      switch (p) {
        case 'idle': return 0;
        case 'starting':
        case 'confirming': return 1;
        case 'url_received':
        case 'waiting_for_code': return 2;
        case 'complete': return 4;
        default: return 0;
      }
    }
    switch (p) {
      case 'idle': return 0;
      case 'starting':
      case 'confirming': return 1;
      case 'url_received':
      case 'waiting_for_code': return 2;
      case 'submitting_code': return 3;
      // 'complete' returns 5 so all 5 step rows (indices 0–4) render done.
      // No row is active in the success terminal state.
      case 'complete': return 5;
      default: return 0;
    }
  });

  isErrorActive = computed(() => this.authPhase() === 'error');

  // Step descriptors for the checklist — branches on handler flow mode so
  // poll flows (Codex device-code, Cursor url-poll) skip the "Verify code"
  // row that doesn't apply. Falls back to the paste-code list when no active
  // handler is resolved (e.g., during the idle dialog-open window) — that's
  // the legacy default and avoids a flicker between 4- and 5-row layouts.
  steps = computed<ReadonlyArray<{ label: string }>>(() => {
    if (this.isPollFlow()) {
      return [
        { label: 'Start' },
        { label: 'Initialize session' },
        { label: 'Authorize in browser' },
        { label: 'Complete' }
      ];
    }
    return [
      { label: 'Start' },
      { label: 'Initialize session' },
      { label: 'Authorize in browser' },
      { label: 'Verify code' },
      { label: 'Complete' }
    ];
  });

  // # Output buffer — accumulates terminal chunks for pattern matching
  #outputBuffer = '';
  #stallTimer: ReturnType<typeof setTimeout> | null = null;
  #copyResetTimer: ReturnType<typeof setTimeout> | null = null;
  #codeResetTimer: ReturnType<typeof setTimeout> | null = null;
  // Tracks which `postAuthScreens` entries have fired for the active
  // agent run. Keys are screen indices (stringified). Reset on every
  // `#advanceWalker` / `#resetState` so a Retry replays the sequence.
  #firedPostAuthScreens = new Set<number>();

  // Live signal — true once the terminal's SSH/PTY is responsive. Drives
  // both the Start button gate (button stays disabled until the underlying
  // container is reachable, not just the xterm widget mounted) and the
  // post-click waiter pipeline below.
  terminalConnected = computed(() => this.terminalRef()?.isConnected() ?? false);

  // RxJS view of the same signal — used in `onStartAuth$.pipe(...)` so we
  // can `take(1)` the first connected emission instead of guessing latency.
  #terminalConnected$ = toObservable(this.terminalConnected);

  // # State resolver
  state = this.$connect({
    open: this.open$,
    serviceStackId: this.serviceStackId$,
    containerId: this.containerId$,
    formState: this.formState$,
    projectName: this.projectName$,
    serviceName: this.serviceName$
  });

  // # Dispatcher
  constructor() {
    super();

    // Latch the most-recent non-error phase. Must run inside the constructor
    // (injection context) BEFORE $dispatchActions so the latch is wired up
    // before any phase signal flips.
    effect(() => {
      const p = this.authPhase();
      if (p !== 'error') this.lastNonErrorPhase.set(p);
    }, { allowSignalWrites: true });

    this.$dispatchActions([
      // Close dialog — passes authSuccess if at least one agent succeeded.
      // Per-agent markAuthorized is dispatched directly by the walker in
      // Stage 1 of `onAuthComplete$`, so the close meta no longer carries
      // an `agentType` to re-fire it (the close-meta-driven dispatch path
      // was removed; it raced with the walker's dispatch on the in-flight
      // entity cache and made the backend reject the second addOne as a
      // duplicate-key error).
      this.onClose$.pipe(
        withLatestFrom(this.serviceStackId$, this.successNavigation$),
        mergeMap(([ _, serviceStackId, successNavigation ]) => {
          const authSuccess = this.hasAnySuccess() || this.authPhase() === 'complete';
          const close = zefDialogClose({
            key: FEATURE_NAME,
            meta: authSuccess
              ? { authSuccess: true, serviceStackId }
              : undefined
          });
          return authSuccess && serviceStackId && successNavigation !== 'none'
            ? [ close, zefGo([ '/service-stack', serviceStackId, 'control-plane' ], { openIde: '1' }) ]
            : [ close ];
        })
      ),

      // Delay terminal rendering until dialog open animation completes
      // Material Dialog's afterOpened fires post-animation; add extra margin
      // so xterm measures correct container dimensions (prevents garbled text)
      this.onDialogOpened$.pipe(
        delay(DIALOG_OPEN_TERMINAL_MOUNT_DELAY_MS),
        tap(() => this.terminalReady.set(true)),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Initialize the walker queue on the dialog's closed→open transition.
      // `agentTypes` (1G+) is preferred; falls back to a 1-element list
      // built from `agentType` for legacy single-agent callers.
      //
      // Gating on the open boolean transition (not on every `meta$` emission)
      // is critical for re-entrant safety: if anything re-dispatches
      // `zefDialogOpen` while we're already open — or any effect produces a
      // fresh dialog-slice ref under us — a meta-only listener would re-fire
      // mid-walker and wipe `currentAgentIdx` + `agentStatuses` back to
      // initial. That bug presented as "second agent's success silently
      // reverts to the first": Codex finishes, the reset fires under us,
      // `activeAgent` snaps back to `claude-code` (idx 0), and the dialog
      // re-renders Claude's complete state on top of an already-authed Codex.
      // The close-reset pipe below wipes the walker on close, so this only
      // needs to seed it on each genuine open.
      this.dialogState$.pipe(
        map((data) => ({
          open: !!data?.state,
          meta: data?.meta as ZcpAgentAuthDialogMeta | undefined
        })),
        distinctUntilChanged((a, b) => a.open === b.open),
        filter((v): v is { open: true; meta: ZcpAgentAuthDialogMeta & { agentType: ZagentAgentType } } =>
          v.open && !!v.meta?.agentType
        ),
        map(({ meta }) => meta.agentTypes && meta.agentTypes.length > 0
          ? meta.agentTypes
          : [ meta.agentType ]),
        tap((queue) => {
          this.agentQueue.set(queue);
          this.currentAgentIdx.set(0);
          this.agentStatuses.set({});
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Reset state when dialog closes (delayed to outlast close animation).
      // Gate on open→closed transitions only — without distinctUntilChanged the
      // pipe re-fires on every unrelated store tick while closed and could wipe
      // a freshly-reopened dialog after the delay. Walker signals are wiped here
      // too so the strip doesn't flash stale authed/active marks on the next open.
      this.dialogState$.pipe(
        map((data) => !!data?.state),
        distinctUntilChanged(),
        filter((open) => !open),
        delay(DIALOG_CLOSE_RESET_DELAY_MS),
        tap(() => {
          this.terminalReady.set(false);
          this.agentQueue.set([]);
          this.currentAgentIdx.set(0);
          this.agentStatuses.set({});
          this.#resetState();
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Handle terminal output — buffered pattern matching
      this.onTerminalOutput$.pipe(
        tap((chunk) => {
          this.#handleOutputChunk(chunk);
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Start auth flow — wait reactively for the terminal WebSocket to report
      // connected, then send the handler's login command. switchMap cancels any
      // pending wait if the user re-clicks Start. The inner tap guards against
      // late completion after Cancel/Reset by checking phase is still 'starting'.
      this.onStartAuth$.pipe(
        tap(() => {
          this.authState.set({ phase: 'starting' });
          this.#outputBuffer = '';
        }),
        switchMap(() => {
          const handler = this.activeHandler();
          if (!handler) {
            this.authState.set({ phase: 'error', message: 'No auth handler configured for this agent type.' });
            return EMPTY;
          }
          return this.#terminalConnected$.pipe(
            filter(Boolean),
            take(1),
            timeout({ each: TERMINAL_CONNECT_TIMEOUT_MS, with: () => of(false) }),
            tap((connected) => {
              if (this.authPhase() !== 'starting') return;
              if (connected) {
                this.#sendToTerminal(handler.loginCommand);
                this.authState.set({ phase: 'confirming' });
              } else {
                this.authState.set({ phase: 'error', message: 'Terminal connection timed out. Please try again.' });
              }
            })
          );
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Cancel auth flow — send Ctrl+C and return to idle
      this.onCancel$.pipe(
        tap(() => {
          this.#sendToTerminal('\u0003');
          this.#resetState();
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Open auth URL in new tab
      this.onOpenUrl$.pipe(
        tap(() => {
          const url = this.authUrl();
          if (url) {
            this.#document.defaultView?.open(url, '_blank');
            this.authState.set({ phase: 'waiting_for_code', authUrl: url });
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Copy auth URL to clipboard with transient "Copied!" feedback
      this.onCopyUrl$.pipe(
        tap(async () => {
          const url = this.authUrl();
          if (!url) return;
          try {
            await navigator.clipboard.writeText(url);
            this.copied.set(true);
            if (this.#copyResetTimer) clearTimeout(this.#copyResetTimer);
            this.#copyResetTimer = setTimeout(() => this.copied.set(false), COPY_FEEDBACK_DURATION_MS);
          } catch {
            // Secure-context / permissions failure — terminal output remains a fallback
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Copy device code to clipboard (Codex device-auth flow). Same feedback
      // pattern as copy URL — independent timer so the two copy indicators
      // don't fight each other if the user mashes both buttons.
      this.onCopyCode$.pipe(
        tap(async () => {
          const code = this.deviceCode();
          if (!code) return;
          try {
            await navigator.clipboard.writeText(code);
            this.copiedCode.set(true);
            if (this.#codeResetTimer) clearTimeout(this.#codeResetTimer);
            this.#codeResetTimer = setTimeout(() => this.copiedCode.set(false), COPY_FEEDBACK_DURATION_MS);
          } catch {
            // Same fallback as URL copy — terminal pane shows the code verbatim
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Send auth code to terminal — value sourced from the ngrx-forms state;
      // formValueOnValid gates emissions on form validity (non-empty required).
      this.onSendCode$.pipe(
        formValueOnValid(this.#form),
        tap(({ code }) => {
          this.authState.set({ phase: 'submitting_code', authUrl: this.authUrl() });
          this.#sendToTerminal(code.trim());
          setTimeout(() => this.#sendToTerminal('\r'), SEND_CODE_NEWLINE_DELAY_MS);
        }),
        mergeMap(() => [ this.#form.reset(), this.#form.setDefaultValues() ])
      ),

      // Retry — reset state
      this.onRetry$.pipe(
        tap(() => this.#resetState()),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Walker advance on success — two-stage pipeline using a SHARED
      // captured snapshot to avoid the race described in the adversarial
      // review: if `meta$` re-emits during the AGENT_AUTH_PAUSE_MS window
      // (queue change while pause is in flight), the delayed advance must
      // refuse to mutate state under the assumption that the captured
      // session is still valid.
      //
      // Stage 1 (immediate, synchronous):
      //   - Mark the captured agent `done` in the strip
      //   - Dispatch per-agent markAuthorized (writes ZCP_AGENT_OAUTH_<SUFFIX>)
      // Stage 2 (delayed AGENT_AUTH_PAUSE_MS):
      //   - If the captured agent is STILL the active agent + idx is
      //     unchanged → advance (or auto-close for single-agent)
      //   - Otherwise → bail (walker session was reset by meta$ re-emit
      //     during the pause; the new session owns the next advance)
      //
      // Legacy single-agent flows (queue.length === 1) auto-close at the
      // end of Stage 2.
      this.onAuthComplete$.pipe(
        filter(() => this.authPhase() === 'complete'),
        withLatestFrom(this.serviceStackId$),
        mergeMap(([ _, serviceStackId ]) => {
          const capturedAgent = this.activeAgent();
          if (!capturedAgent || !serviceStackId) return [];

          this.agentStatuses.update((s) => ({ ...s, [capturedAgent]: 'done' }));

          return [
            zcpAgentAuthDialogActions.markAuthorized({
              serviceStackId,
              agentType: capturedAgent
            })
          ];
        })
      ),

      // After the per-agent pause, either advance to the next agent or
      // (single-agent only) auto-close the dialog. Captures session state
      // at emit time and bails if the walker session changed during the
      // pause. Idx capture + compare uses `#agentIdxForSession()` which
      // tracks the same clamped value used by `activeAgent()` — comparing
      // raw `currentAgentIdx()` could spuriously pass or fail when the
      // clamp diverges from the underlying signal during a queue shrink.
      this.onAuthComplete$.pipe(
        filter(() => this.authPhase() === 'complete'),
        withLatestFrom(this.serviceStackId$, this.successNavigation$),
        map(([ _, serviceStackId, successNavigation ]) => ({
          serviceStackId,
          successNavigation,
          capturedAgent: this.activeAgent(),
          capturedIdx: this.#safeAgentIdx(),
          capturedQueueLen: this.agentQueue().length,
          capturedIsSingle: this.isSingleAgent()
        })),
        delay(AGENT_AUTH_PAUSE_MS),
        switchMap(({ serviceStackId, successNavigation, capturedAgent, capturedIdx, capturedQueueLen, capturedIsSingle }) => {
          // Bail if the walker session is no longer the one that emitted —
          // i.e., meta$ re-emitted a new queue during the pause and reset
          // the walker. Comparing the snapshot to current state.
          const stillSameSession =
            this.activeAgent() === capturedAgent
            && this.#safeAgentIdx() === capturedIdx
            && this.agentQueue().length === capturedQueueLen;
          if (!stillSameSession) return [];

          if (capturedIsSingle) {
            return this.#closeAndNavigate(serviceStackId, successNavigation);
          }

          // Multi-agent: advance to the next agent (or no-op if this was
          // the last one). Template re-renders Strip + CTAs from local
          // signal updates — `#advanceWalker` doesn't dispatch directly.
          this.#advanceWalker();

          // After the LAST agent advances (a no-op call), check whether
          // the walker terminated with every agent successful — if so,
          // mirror single-agent's auto-close + auto-navigate UX so the
          // success path feels consistent across single and multi. Any
          // failure or skip leaves the dialog open with the manual
          // "Go to Control Panel" CTA so the user can see the per-agent
          // outcome summary in the strip before navigating.
          if (this.walkerComplete()) {
            const statuses = this.agentStatuses();
            const allDone = this.agentQueue().every((t) => statuses[t] === 'done');
            if (allDone) {
              return this.#closeAndNavigate(serviceStackId, successNavigation);
            }
          }
          return [];
        })
      ),

      // Skip the current agent — mark `failed` if we're in the error CTA
      // (user gave up on retry) or `skipped` for any other path. Advances
      // to the next agent (or finalizes when this was the last one).
      this.onSkip$.pipe(
        tap(() => {
          const active = this.activeAgent();
          if (active) {
            const status: AgentStripStatus = this.authPhase() === 'error'
              ? 'failed'
              : 'skipped';
            this.agentStatuses.update((s) => ({ ...s, [active]: status }));
          }
          this.#advanceWalker();
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Final terminal CTA — close dialog and let `onAuthSuccessNavigate$` route
      // to the control plane page if any agent succeeded. agentType is omitted
      // for walker closes — per-agent markAuthorized already fired during
      // the walker run.
      this.onGoToControlPanel$.pipe(
        withLatestFrom(this.serviceStackId$, this.successNavigation$),
        mergeMap(([ _, serviceStackId, successNavigation ]) => {
          const authSuccess = this.hasAnySuccess();
          const close = zefDialogClose({
            key: FEATURE_NAME,
            meta: { authSuccess, serviceStackId }
          });
          return authSuccess && serviceStackId && successNavigation !== 'none'
            ? [ close, zefGo([ '/service-stack', serviceStackId, 'control-plane' ], { openIde: '1' }) ]
            : [ close ];
        })
      )
    ]);

    // Clean up stall timer on component destroy
    this.#destroyRef.onDestroy(() => {
      if (this.#stallTimer) {
        clearTimeout(this.#stallTimer);
        this.#stallTimer = null;
      }
      if (this.#copyResetTimer) {
        clearTimeout(this.#copyResetTimer);
        this.#copyResetTimer = null;
      }
      if (this.#codeResetTimer) {
        clearTimeout(this.#codeResetTimer);
        this.#codeResetTimer = null;
      }
    });
  }

  // Resets in-flow auth state. Intentionally does NOT touch terminalReady —
  // Cancel/Retry within the same dialog session must leave the terminal mounted
  // so the next Start click can wait on the existing connection instead of a
  // destroyed `viewChild`. terminalReady is reset by the dialog-close pipe.
  // Also leaves walker signals (`agentQueue` / `currentAgentIdx` / `agentStatuses`)
  // untouched — those are owned by `#advanceWalker` and the dialog-open meta pipe.
  #resetState() {
    this.authState.set({ phase: 'idle' });
    this.copied.set(false);
    this.copiedCode.set(false);
    this.#outputBuffer = '';
    this.#firedPostAuthScreens.clear();
    clearTimeout(this.#stallTimer);
    this.#stallTimer = null;
    if (this.#copyResetTimer) {
      clearTimeout(this.#copyResetTimer);
      this.#copyResetTimer = null;
    }
    if (this.#codeResetTimer) {
      clearTimeout(this.#codeResetTimer);
      this.#codeResetTimer = null;
    }
    this.#store.dispatch(this.#form.reset());
    this.#store.dispatch(this.#form.setDefaultValues());
  }

  // Atomic success-close: dispatches both `zefDialogClose` (with
  // authSuccess meta for any downstream consumers that still care) AND —
  // unless the caller's `successNavigation` policy opts out — the
  // navigation to the control-plane page with `openIde=1`.
  // Previously the navigation was a separate effect listening to the
  // close meta — that chain was unreliable on the multi-agent path
  // (dialog closed but redirect never fired), so we dispatch the route
  // directly from the same site that decides to close. The control-plane
  // page picks up `openIde=1` and activates the inline Cloud IDE
  // (code-server) pane — a caller that already owns a prewarmed fullscreen
  // embed (the onboarding wizard) passes 'none' so this can't silently
  // re-dock it underneath the wizard layer.
  #closeAndNavigate(serviceStackId: string, successNavigation: ZcpAgentAuthDialogSuccessNavigation) {
    const close = zefDialogClose({
      key: FEATURE_NAME,
      meta: { authSuccess: true, serviceStackId }
    });
    return successNavigation === 'none'
      ? [ close ]
      : [ close, zefGo([ '/service-stack', serviceStackId, 'control-plane' ], { openIde: '1' }) ];
  }

  // Walker advance — used after success (current agent marked `done`) AND
  // skip (current agent marked `skipped`). If the queue has more agents,
  // bumps `currentAgentIdx` and resets the inner state machine so the next
  // agent starts from `idle`. If this was the last agent, leaves the queue
  // alone — the template branches on `walkerComplete()` to show the final
  // "Go to Control Panel" CTA.
  //
  // Reads from `#safeAgentIdx` (clamped) so a transient out-of-bounds
  // `currentAgentIdx` (mid-queue-shrink) doesn't compute an incorrect
  // advance — the next idx is the clamped one + 1, never `queue.length`.
  #advanceWalker() {
    const idx = this.#safeAgentIdx();
    const queue = this.agentQueue();
    if (idx + 1 < queue.length) {
      this.currentAgentIdx.set(idx + 1);
      this.#resetState();
      // Each agent gets a fresh shell session. Previous agent's CLI output
      // (e.g. Claude's "Login successful. Press Enter to continue…") would
      // otherwise persist in the xterm scrollback and the next command would
      // run in a pty that still has the prior CLI's TUI mode set. Toggling
      // `terminalReady` unmounts z-terminal, closes its WebSocket, then a
      // remount opens a fresh shell on the same container.
      this.terminalReady.set(false);
      setTimeout(() => this.terminalReady.set(true), TERMINAL_REMOUNT_DELAY_MS);
    }
    // else: at end of queue — `walkerComplete()` is now true, template renders
    // Go-to-Control-Panel CTA. We deliberately keep the inner state machine
    // at `complete` (or whatever terminal phase) so a re-render of the final
    // step row stays accurate for the last agent.
  }

  // # Output processing — uses handler's patterns
  #handleOutputChunk(chunk: string) {
    const phase = this.authPhase();
    const handler = this.activeHandler();

    // Only process during active auth flow with a valid handler
    if (phase === 'idle' || phase === 'complete' || !handler) return;

    // Accumulate buffer
    this.#outputBuffer += chunk;

    const parsed = parseTerminalOutput(this.#outputBuffer);
    const clean = parsed.clean;

    // Detect success or error — check in ALL active phases
    // Handles: user typing code directly in terminal, browser auto-callback, etc.
    if (handler.authSuccessPattern.test(clean)) {
      this.authState.set({ phase: 'complete' });
      this.#outputBuffer = '';
      clearTimeout(this.#stallTimer);
      this.onAuthComplete$.next();
      return;
    }
    // Only check error pattern in post-URL phases (avoid false positives during TUI navigation)
    if (phase === 'submitting_code' || phase === 'waiting_for_code' || phase === 'url_received') {
      if (handler.authErrorPattern.test(clean)) {
        this.authState.set({ phase: 'error', message: 'Authentication failed. The code may be invalid or expired.' });
        this.#outputBuffer = '';
        return;
      }
    }

    // Phase: confirming/starting — auto-respond to Y/N prompts
    if (phase === 'confirming' || phase === 'starting') {
      if (handler.confirmPromptPattern.test(clean)) {
        this.#sendToTerminal('y\n');
        this.#outputBuffer = '';
        return;
      }
    }

    // Phase: confirming — detect auth URL and/or "paste code" prompt
    if (phase === 'confirming') {
      const urlResult = matchAuthUrl(parsed, handler.authUrlPattern);

      if (urlResult.status === 'pending') {
        // A URL candidate ends exactly at the buffer edge — more bytes may extend
        // it. Acting now is the truncation bug; hold and let the next chunk decide.
        clearTimeout(this.#stallTimer);
        return;
      }

      if (urlResult.status === 'complete') {
        const url = urlResult.value;

        // Device-code flow (Codex): extract the device code and enter
        // waiting_for_code WITHOUT auto-opening the URL. The dialog
        // displays the code prominently + a manual Open button so the
        // user has time to copy the code before navigating to the page.
        //
        // The URL and the device code arrive in separate output chunks
        // (URL line first, then "Enter this one-time code" then the code
        // on its own line). If we transition the moment the URL matches,
        // `deviceCode` is null because the code chunk hasn't arrived yet
        // and the dialog renders an empty code field. Hold the phase in
        // `confirming` until BOTH match, accumulating buffer between
        // chunks. Stall timer doesn't fire mid-stream so this can't lock.
        if (handler.flowMode === 'device-code' && handler.deviceCodePattern) {
          const codeResult = matchCompletedToken(clean, handler.deviceCodePattern, DEVICE_CODE_CONTINUATION);
          if (codeResult.status !== 'complete') {
            // Suppress the stall while we wait for the code chunk —
            // pressing Enter into Codex mid-device-auth could submit
            // garbage and break the flow.
            clearTimeout(this.#stallTimer);
            return;
          }
          const deviceCode = codeResult.value;
          this.authState.set({ phase: 'waiting_for_code', authUrl: url, deviceCode });
          this.#outputBuffer = '';
          clearTimeout(this.#stallTimer);
          return;
        }

        // Paste-code flow (Claude): auto-open the URL so the user lands
        // on the consent screen immediately. Browser code copy + paste
        // back into the dialog follows.
        const opened = this.#document.defaultView?.open(url, '_blank');
        if (opened) {
          this.authState.set({ phase: 'waiting_for_code', authUrl: url });
        } else {
          this.authState.set({ phase: 'url_received', authUrl: url });
        }
        this.#outputBuffer = '';
        clearTimeout(this.#stallTimer);
        return;
      }

      // Fallback: "paste code here" without URL match — let user copy URL from terminal
      if (handler.pasteCodePattern.test(clean)) {
        this.authState.set({ phase: 'url_received', authUrl: null });
        this.#outputBuffer = '';
        clearTimeout(this.#stallTimer);
        return;
      }
    }

    // Post-auth screen navigation: for handlers that declare `postAuthScreens`,
    // walk the unfired entries in order and fire the first one whose `detect`
    // matches the current buffer. Each entry fires at most once per agent run
    // (`#firedPostAuthScreens` tracks indices, cleared in `#resetState`).
    // Buffer is cleared after a send so the next screen's detect starts fresh.
    //
    // `sendKeys` array form: each item is sent sequentially with `sendKeyDelayMs`
    // (default 100ms) between writes — Ink/Bubble-Tea TUIs need a render frame
    // between key events when navigating widget focus. Sending the whole burst
    // in one write lands the final Enter on stale focus state.
    if (phase === 'submitting_code' && handler.postAuthScreens?.length) {
      for (let i = 0; i < handler.postAuthScreens.length; i++) {
        if (this.#firedPostAuthScreens.has(i)) continue;
        const screen = handler.postAuthScreens[i];
        if (screen.detect.test(clean)) {
          const isSequence = Array.isArray(screen.sendKeys);
          this.#firedPostAuthScreens.add(i);
          if (isSequence) {
            const items = screen.sendKeys as string[];
            const delay = screen.sendKeyDelayMs ?? POST_AUTH_KEY_DELAY_MS;
            items.forEach((item, j) => {
              setTimeout(() => this.#sendToTerminal(item), j * delay);
            });
          } else {
            this.#sendToTerminal(screen.sendKeys as string);
          }
          this.#outputBuffer = '';
          clearTimeout(this.#stallTimer);
          return;
        }
      }
    }

    // Stall detection: no pattern matched → send enter after timeout. This
    // is the PRIMARY mechanism for navigating unknown TUI screens.
    // Handler can opt into firing during `submitting_code` too (Antigravity-
    // style multi-screen post-auth setup) via `stallDuringSubmission: true`.
    const stallActive =
      phase === 'confirming'
      || phase === 'starting'
      || (phase === 'submitting_code' && handler.stallDuringSubmission === true);
    if (parsed.endsInsideEscape) {
      // Mid-escape tail — never start a stall countdown off an incomplete render.
      clearTimeout(this.#stallTimer);
    } else if (stallActive) {
      clearTimeout(this.#stallTimer);
      this.#stallTimer = setTimeout(() => {
        const livePhase = this.authPhase();
        const stillActive =
          livePhase === 'confirming'
          || livePhase === 'starting'
          || (livePhase === 'submitting_code' && handler.stallDuringSubmission === true);
        if (stillActive) {
          this.#sendToTerminal('\r');
          this.#outputBuffer = '';
        }
      }, STALL_TIMEOUT_MS);
    }

    // Prevent unbounded buffer growth — keep last 5000 chars (long OAuth URLs need
    // space). A mid-escape tail defers the trim (slicing could cut the sequence in
    // half), but only up to a hard ceiling so a stream that never terminates an
    // escape can't grow the buffer — and the per-chunk re-parse — without bound.
    if (this.#outputBuffer.length > (parsed.endsInsideEscape ? 20000 : 5000)) {
      this.#outputBuffer = this.#outputBuffer.slice(-4000);
    }
  }

  #sendToTerminal(payload: string): void {
    this.terminalRef()?.send(payload);
  }
}
